import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { adminApi, CreateUserRequest, CreateTariffRequest, CreateProductRequest, Source, UK, CreateGeneralTariffRequest, UpdateGeneralTariffRequest } from '../services/adminApi';
import { normalizeApiBaseUrl } from '../services/api';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { getCityNameById } from '../utils/cityUtils';
import {
  UserPlusIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';


interface User {
  id: string;
  login: string;
  type: 'admin' | 'manager' | 'client' | 'salary';
  agentId: number;
  category?: number;
  salaryCategories?: number[];
  tariffId?: string;
  sources?: Array<{
    id: string;
    source: {
      id: string;
      name: string;
    };
  }>;
  uks?: Array<{
    id: string;
    uk: {
      id: string;
      name: string;
    };
  }>;
  createdAt: string;
}

const USER_CITY_OPTIONS = [
  { label: 'Казань', value: '0' },
  { label: 'Нижний Новгород', value: '14' },
  { label: 'Уфа', value: '16' },
  { label: 'Пенза', value: '18' },
  { label: 'Чебоксары', value: '40' },
  { label: 'Саранск', value: '46' },
  { label: 'Ижевск', value: '50' },
  { label: 'Красноярск', value: '52' },
  { label: 'Набережные Челны', value: '56' },
  { label: 'Мастер сервис', value: '60' },
  { label: 'Рязань', value: '58' },
  { label: 'Йошкар-Ола', value: '34' },
  { label: 'Тест', value: '64' },
  { label: 'Города', value: '12' },
  { label: 'Иваново', value: '125' },
  { label: 'Кохма', value: '127' },
  { label: 'Москва', value: '20' },
  { label: 'Пушкино', value: '111' },
];

interface TariffPlan {
  id: string;
  name: string;
  uks?: Array<{
    id: string;
    uk: {
      id: string;
      name: string;
    };
  }>;
  waterRate: number;
  heatRate: number;
  gasRate: number;
  description?: string;
  isActive: boolean;
  sources?: Array<{
    sourceId: string;
    source: {
      id: string;
      name: string;
    };
  }>;
  products?: Array<{
    productId: string;
    rate: number;
    rateType: 'fixed' | 'percentage';
    product: {
      id: string;
      name: string;
      type: string;
    };
  }>;
}

interface Product {
  id: string;
  name: string;
  type: 'water' | 'warm' | 'gas' | 'other';
  description?: string;
  isActive: boolean;
  isHidden: boolean;
  hiddenForClientIds?: string[];
}

type ProductFormState = {
  name: string;
  type: 'water' | 'warm' | 'gas' | 'other';
  description: string;
  hiddenForClientIds: string[];
  hideForAll: boolean;
};

const initialProductForm: ProductFormState = {
  name: '',
  type: 'water',
  description: '',
  hiddenForClientIds: [],
  hideForAll: false,
};

const AdminPanel: React.FC = () => {
  const { user } = useAuth();

  // Функция генерации пароля
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!?';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setUserForm({ ...userForm, password });
  };
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState<User[]>([]);
  const [tariffs, setTariffs] = useState<TariffPlan[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [uks, setUks] = useState<UK[]>([]);
  const [generalTariff, setGeneralTariff] = useState<any>(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showTariffForm, setShowTariffForm] = useState(false);
  const [showGeneralTariffForm, setShowGeneralTariffForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showSourceForm, setShowSourceForm] = useState(false);
  const [showUKForm, setShowUKForm] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [ukSearchTerm, setUKSearchTerm] = useState('');

  // Форма пользователя
  const [userForm, setUserForm] = useState<{
    login: string;
    password: string;
    type: 'admin' | 'manager' | 'client' | 'salary';
    agentId: string;
    category: string;
    salaryCategories: string[];
    ukIds: string[];
    sourceIds: string[];
    disableRewards?: boolean;
  }>({
    login: '',
    password: '',
    type: 'client',
    agentId: '',
    category: '',
    salaryCategories: [],
    ukIds: [],
    sourceIds: [],
    disableRewards: false,
  });

  // Настройки отображения дополнительных полей
  const [displaySettings, setDisplaySettings] = useState<{
    showAccountNumber: boolean;
    showComments: boolean;
    showRefusalReason: boolean;
    showCounterType: boolean;
    showSerialNumber: boolean;
    showReadings: boolean;
    showDealId: boolean;
    showTPlusReport: boolean;
  }>({
    showAccountNumber: false,
    showComments: false,
    showRefusalReason: false,
    showCounterType: false,
    showSerialNumber: false,
    showReadings: false,
    showDealId: false,
    showTPlusReport: false,
  });

  // Форма тарифа
  const [tariffForm, setTariffForm] = useState({
    name: '',
    ukIds: [] as string[],
    waterRate: 0,
    heatRate: 0,
    gasRate: 0,
    description: '',
    sourceIds: [] as string[],
    products: [] as Array<{
      productId: string;
      rate: number;
      rateType: 'fixed' | 'percentage';
    }>
  });

  // Форма общего тарифа
  const [generalTariffForm, setGeneralTariffForm] = useState({
    waterRate: 0,
    heatRate: 0,
    gasRate: 0,
    products: [] as Array<{
      productId: string;
      rate: number;
      rateType: 'fixed' | 'percentage';
    }>
  });

  // Форма товара
  const [productForm, setProductForm] = useState<ProductFormState>(initialProductForm);

  // Форма источника
  const [sourceForm, setSourceForm] = useState({
    name: '',
    sort: 0,
    statusId: '',
  });

  // Форма УК
  const [ukForm, setUKForm] = useState({
    name: '',
  });

  const clientOptions = useMemo(
    () =>
      users
        .filter((user) => user.type === 'client')
        .map((client) => ({
          label: client.login,
          value: client.id,
        })),
    [users]
  );

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    loadData();
  }, []);


  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, tariffsRes, productsRes, sourcesRes, uksRes, generalTariffRes] = await Promise.all([
        adminApi.getUsers(),
        adminApi.getTariffs(),
        adminApi.getProducts(),
        adminApi.getSources(),
        adminApi.getUKs(),
        adminApi.getGeneralTariff().catch(() => null), // Может не существовать
      ]);

      setUsers(usersRes.data || []);
      setTariffs(tariffsRes.data || []);
      setProducts(productsRes.data || []);
      setSources(sourcesRes.data || []);
      setUks(uksRes.data || []);
      setGeneralTariff(generalTariffRes?.data || null);
    } catch (error) {
      console.error('Error loading data:', error);
      // Устанавливаем пустые массивы при ошибке
      setUsers([]);
      setTariffs([]);
      setProducts([]);
      setSources([]);
      setUks([]);
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация источников по поисковому запросу
  const filteredSources = sources.filter(source =>
    source.name.toLowerCase().includes(sourceSearchTerm.toLowerCase()) ||
    source.statusId.toLowerCase().includes(sourceSearchTerm.toLowerCase()) ||
    source.sort.toString().includes(sourceSearchTerm)
  );

  // Фильтрация УК по поисковому запросу
  const filteredUKs = uks.filter(uk =>
    uk.name.toLowerCase().includes(ukSearchTerm.toLowerCase())
  );

  // Проверка доступа
  if (!user || user.type !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleCreateUser = async () => {
    // Валидация обязательных полей
    if (!userForm.login || (!editingItem && !userForm.password) || !userForm.type || !userForm.agentId) {
      alert('Пожалуйста, заполните все обязательные поля');
      return;
    }
    if (userForm.password && userForm.password.length < 8) {
      alert('Пароль должен содержать минимум 8 символов');
      return;
    }

    if ((userForm.type === 'manager' || userForm.type === 'client') && userForm.category === '') {
      alert('Пожалуйста, выберите город для пользователя');
      return;
    }
    if (!editingItem && userForm.type === 'salary' && userForm.salaryCategories.length === 0) {
      alert('Пожалуйста, выберите хотя бы один город для пользователя типа Зарплата');
      return;
    }

    // Дополнительная валидация для клиентов
    if (userForm.type === 'client') {
      if (!userForm.sourceIds || userForm.sourceIds.length === 0) {
        alert('Пожалуйста, выберите хотя бы один источник для клиента');
        return;
      }
      // УК теперь необязательно для клиентов
    }

    // Дополнительная валидация для менеджеров
    if (userForm.type === 'manager') {
      if (!userForm.ukIds || userForm.ukIds.length === 0) {
        alert('Пожалуйста, выберите хотя бы одну УК для менеджера');
        return;
      }
      if (!userForm.sourceIds || userForm.sourceIds.length === 0) {
        alert('Пожалуйста, выберите хотя бы один источник для менеджера');
        return;
      }
    }

    try {
      if (editingItem) {
        // При редактировании не отправляем пустой пароль
        const parsedAgentId = Number(userForm.agentId);
        if (!Number.isInteger(parsedAgentId) || parsedAgentId <= 0) {
          alert('ID должен быть положительным целым числом');
          return;
        }

        const existingAgent = users.find((user) => user.agentId === parsedAgentId && user.id !== editingItem.id);
        if (existingAgent) {
          alert(`Пользователь с ID ${parsedAgentId} уже существует`);
          return;
        }

        const updateData: any = {
          id: editingItem.id,
          login: userForm.login,
          type: userForm.type,
          agentId: parsedAgentId,
          category: (userForm.type === 'admin' || userForm.type === 'salary')
            ? undefined
            : (userForm.category === '' ? undefined : parseInt(userForm.category as string)),
          salaryCategories: userForm.type === 'salary'
            ? userForm.salaryCategories.map((value) => parseInt(value, 10))
            : undefined,
          ukIds: (userForm.type === 'client' || userForm.type === 'manager') ? userForm.ukIds : undefined,
          sourceIds: (userForm.type === 'client' || userForm.type === 'manager') ? userForm.sourceIds : undefined,
          disableRewards: userForm.disableRewards || false,
        };

        // Добавляем пароль только если он не пустой
        if (userForm.password) {
          updateData.password = userForm.password;
        }

        await adminApi.updateUser(updateData);
        
        // Сохраняем настройки отображения полей
        const token = localStorage.getItem('access_token');
        const API_BASE_URL = normalizeApiBaseUrl(
          (import.meta as any).env?.VITE_API_URL ||
            (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://poverkaagent.ru'),
        );
        
        await fetch(`${API_BASE_URL}/api/v1/admin/users/${editingItem.id}/display-settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(displaySettings),
        });
      } else {
        const parsedAgentId = Number(userForm.agentId);
        if (!Number.isInteger(parsedAgentId) || parsedAgentId <= 0) {
          alert('ID должен быть положительным целым числом');
          return;
        }

        const existingAgent = users.find((user) => user.agentId === parsedAgentId);
        if (existingAgent) {
          alert(`Пользователь с ID ${parsedAgentId} уже существует`);
          return;
        }

        const userData: CreateUserRequest = {
          login: userForm.login,
          password: userForm.password,
          type: userForm.type,
          agentId: parsedAgentId,
          category: (userForm.type === 'admin' || userForm.type === 'salary')
            ? undefined
            : (userForm.category === '' ? undefined : parseInt(userForm.category as string)),
          salaryCategories: userForm.type === 'salary'
            ? userForm.salaryCategories.map((value) => parseInt(value, 10))
            : undefined,
          ukIds: (userForm.type === 'client' || userForm.type === 'manager') ? userForm.ukIds : undefined,
          sourceIds: (userForm.type === 'client' || userForm.type === 'manager') ? userForm.sourceIds : undefined,
          disableRewards: userForm.disableRewards || false,
        };
        const createdUserResponse = await adminApi.createUser(userData);
        
        // Сохраняем настройки отображения полей для нового пользователя
        if (createdUserResponse && createdUserResponse.data && createdUserResponse.data.id) {
          const token = localStorage.getItem('access_token');
          const API_BASE_URL = normalizeApiBaseUrl(
            (import.meta as any).env?.VITE_API_URL ||
              (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://poverkaagent.ru'),
          );
          
          await fetch(`${API_BASE_URL}/api/v1/admin/users/${createdUserResponse.data.id}/display-settings`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(displaySettings),
          });
        }
      }

      setShowUserForm(false);
      setEditingItem(null);
      setUserForm({ login: '', password: '', type: 'client', agentId: '', category: '', salaryCategories: [], ukIds: [], sourceIds: [], disableRewards: false });
      setDisplaySettings({
        showAccountNumber: false,
        showComments: false,
        showRefusalReason: false,
        showCounterType: false,
        showSerialNumber: false,
        showReadings: false,
        showDealId: false,
        showTPlusReport: false,
      });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error saving user:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  const handleCreateTariff = async () => {
    // Валидация обязательных полей
    if (!tariffForm.name || tariffForm.sourceIds.length === 0) {
      alert('Пожалуйста, заполните обязательные поля: название тарифа и выберите хотя бы один источник. УК опционально.');
      return;
    }

    try {
      const tariffData: CreateTariffRequest = {
        name: tariffForm.name,
        ukIds: tariffForm.ukIds.length > 0 ? tariffForm.ukIds : undefined,
        waterRate: tariffForm.waterRate,
        heatRate: tariffForm.heatRate,
        gasRate: tariffForm.gasRate,
        description: tariffForm.description || undefined,
        sourceIds: tariffForm.sourceIds || [],
        products: tariffForm.products,
      };

      if (editingItem) {
        await adminApi.updateTariff({
          id: editingItem.id,
          ...tariffData,
        });
      } else {
        await adminApi.createTariff(tariffData);
      }

      setShowTariffForm(false);
      setEditingItem(null);
      setTariffForm({
        name: '',
        ukIds: [],
        waterRate: 0,
        heatRate: 0,
        gasRate: 0,
        description: '',
        sourceIds: [],
        products: []
      });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error saving tariff:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  // Обработчики для общего тарифа
  const handleCreateGeneralTariff = async () => {
    // Валидация обязательных полей - название теперь не требуется

    try {
      const tariffData: CreateGeneralTariffRequest | UpdateGeneralTariffRequest = {
        name: 'Общий тариф', // Фиксированное название
        waterRate: generalTariffForm.waterRate,
        heatRate: generalTariffForm.heatRate,
        gasRate: generalTariffForm.gasRate,
        products: generalTariffForm.products,
      };

      if (generalTariff && generalTariff.id) {
        await adminApi.updateGeneralTariff(tariffData as UpdateGeneralTariffRequest);
      } else {
        await adminApi.createGeneralTariff(tariffData as CreateGeneralTariffRequest);
      }

      setShowGeneralTariffForm(false);
      setGeneralTariffForm({
        waterRate: 0,
        heatRate: 0,
        gasRate: 0,
        products: []
      });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error saving general tariff:', error);
      alert('Ошибка при сохранении общего тарифа');
    }
  };

  const handleEditGeneralTariff = () => {
    if (generalTariff) {
      setGeneralTariffForm({
        waterRate: generalTariff.waterRate || 0,
        heatRate: generalTariff.heatRate || 0,
        gasRate: generalTariff.gasRate || 0,
        products: generalTariff.products?.map((p: any) => ({
          productId: p.productId,
          rate: p.rate,
          rateType: p.rateType
        })) || []
      });
      setShowGeneralTariffForm(true);
    }
  };

  const handleDeleteGeneralTariff = async () => {
    if (generalTariff && generalTariff.id && confirm('Вы уверены, что хотите удалить общий тариф?')) {
      try {
        await adminApi.deleteGeneralTariff();
        setGeneralTariff(null);
        loadData();
      } catch (error) {
        console.error('Error deleting general tariff:', error);
        alert('Ошибка при удалении общего тарифа');
      }
    }
  };

  const handleCreateProduct = async () => {
    // Валидация обязательных полей
    if (!productForm.name || !productForm.type) {
      alert('Пожалуйста, заполните все обязательные поля: название товара и тип');
      return;
    }

    try {
      const productData: CreateProductRequest = {
        name: productForm.name,
        type: productForm.type,
        description: productForm.description || undefined,
        isHidden: productForm.hideForAll,
        hiddenForClientIds: productForm.hiddenForClientIds,
      };

      if (editingItem) {
        await adminApi.updateProduct({
          id: editingItem.id,
          ...productData,
        });
      } else {
        await adminApi.createProduct(productData);
      }

      setShowProductForm(false);
      setEditingItem(null);
      setProductForm(initialProductForm);
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error saving product:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  const handleCreateSource = async () => {
    // Валидация обязательных полей
    if (!sourceForm.name || sourceForm.statusId === '') {
      alert('Пожалуйста, заполните все обязательные поля: название источника и ID статуса');
      return;
    }

    try {
      const sourceData = {
        name: sourceForm.name,
        sort: sourceForm.sort,
        statusId: sourceForm.statusId,
      };

      if (editingItem) {
              await adminApi.updateSource({
        id: editingItem.id,
        name: sourceData.name,
        sort: sourceData.sort,
        statusId: sourceData.statusId,
      } as any);
      } else {
        await adminApi.createSource(sourceData as any);
      }

      setShowSourceForm(false);
      setEditingItem(null);
      setSourceForm({ name: '', sort: 0, statusId: '' });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error saving source:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  const handleCreateUK = async () => {
    // Валидация обязательных полей
    if (!ukForm.name) {
      alert('Пожалуйста, заполните название УК');
      return;
    }

    try {
      const ukData = {
        name: ukForm.name,
      };

      if (editingItem) {
        await adminApi.updateUK({
          id: editingItem.id,
          name: ukData.name,
        } as any);
      } else {
        await adminApi.createUK(ukData as any);
      }

      setShowUKForm(false);
      setEditingItem(null);
      setUKForm({ name: '' });
      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error saving UK:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  // Функция для загрузки настроек отображения полей
  const loadDisplaySettings = async (userId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const API_BASE_URL = normalizeApiBaseUrl(
        (import.meta as any).env?.VITE_API_URL ||
          (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://poverkaagent.ru'),
      );
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/users/${userId}/display-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const settings = await response.json();
        setDisplaySettings({
          showAccountNumber: settings.showAccountNumber || false,
          showComments: settings.showComments || false,
          showRefusalReason: settings.showRefusalReason || false,
          showCounterType: settings.showCounterType || false,
          showSerialNumber: settings.showSerialNumber || false,
          showReadings: settings.showReadings || false,
          showDealId: settings.showDealId || false,
          showTPlusReport: settings.showTPlusReport || false,
        });
      } else {
        // Если настройки не найдены, используем значения по умолчанию
        setDisplaySettings({
          showAccountNumber: false,
          showComments: false,
          showRefusalReason: false,
          showCounterType: false,
          showSerialNumber: false,
          showReadings: false,
          showDealId: false,
          showTPlusReport: false,
        });
      }
    } catch (error) {
      console.error('Error loading display settings:', error);
      // При ошибке используем значения по умолчанию
      setDisplaySettings({
        showAccountNumber: false,
        showComments: false,
        showRefusalReason: false,
        showCounterType: false,
        showSerialNumber: false,
        showReadings: false,
        showDealId: false,
        showTPlusReport: false,
      });
    }
  };

  const handleEdit = (item: any, type: string) => {
    setEditingItem({ ...item, itemType: type });
    if (type === 'user') {
      const { id, ...userData } = item;
      
      // Отладочная информация
      console.log('Editing user data:', userData);
      console.log('User uks:', userData.uks);
      console.log('User sources:', userData.sources);
      
      // Правильно извлекаем ID из связанных данных
      const ukIds = userData.uks?.map((u: any) => u.uk?.id || u.id) || [];
      const sourceIds = userData.sources?.map((s: any) => s.source?.id || s.id) || [];
      
      console.log('Extracted ukIds:', ukIds);
      console.log('Extracted sourceIds:', sourceIds);
      
      // Проверяем, что данные загружены
      console.log('Available uks for selection:', uks);
      console.log('Available sources for selection:', sources);
      
      setUserForm({
        ...userData,
        password: '',
        agentId: userData.agentId?.toString() || '',
        // Для администратора категория всегда пустая, для менеджеров и клиентов - заполняем если есть
        category: (userData.type === 'admin' || userData.type === 'salary')
          ? ''
          : (userData.category !== null && userData.category !== undefined ? userData.category.toString() : ''),
        salaryCategories: (userData.salaryCategories || []).map((value: number) => String(value)),
        ukIds: ukIds,
        sourceIds: sourceIds,
        disableRewards: userData.disableRewards || false,
      });
      
      // Загружаем настройки отображения полей
      loadDisplaySettings(item.id);
      
      setShowUserForm(true);
    } else if (type === 'tariff') {
      const { id: tariffId, ...tariffData } = item;
      setTariffForm({
        name: tariffData.name || '',
        ukIds: tariffData.uks?.map((u: any) => u.uk?.id || u.id) || [],
        waterRate: tariffData.waterRate || 0,
        heatRate: tariffData.heatRate || 0,
        gasRate: tariffData.gasRate || 0,
        description: tariffData.description || '',
        sourceIds: tariffData.sources?.map((s: any) => s.sourceId) || [],
        products: tariffData.products?.map((p: any) => ({
          productId: p.productId,
          rate: p.rate,
          rateType: p.rateType
        })) || []
      });
      setShowTariffForm(true);
    } else if (type === 'product') {
      const { id: productId, ...productData } = item;
      setProductForm({
        name: productData.name || '',
        type: productData.type || 'water',
        description: productData.description || '',
        hiddenForClientIds: productData.hiddenForClientIds || [],
        hideForAll: productData.isHidden || false,
      });
      setShowProductForm(true);
    } else if (type === 'source') {
      const { id: sourceId, ...sourceData } = item;
      setSourceForm({
        name: sourceData.name || '',
        sort: sourceData.sort || 0,
        statusId: sourceData.statusId || ''
      });
      setShowSourceForm(true);
    } else if (type === 'uk') {
      const { id: ukId, ...ukData } = item;
      setEditingItem({ ...item, itemType: 'uk' });
      setUKForm({
        name: ukData.name || '',
      });
      setShowUKForm(true);
    }
  };

  const handleDelete = async (id: string, type: string) => {
    if (!confirm(`Вы уверены, что хотите удалить этот ${type === 'user' ? 'пользователя' : type === 'tariff' ? 'тариф' : type === 'product' ? 'товар' : type === 'source' ? 'источник' : 'УК'}?`)) {
      return;
    }

    try {
      if (type === 'user') {
        await adminApi.deleteUser(id);
      } else if (type === 'tariff') {
        await adminApi.deleteTariff(id);
      } else if (type === 'product') {
        await adminApi.deleteProduct(id);
      } else if (type === 'source') {
        await adminApi.deleteSource(id);
      } else if (type === 'uk') {
        await adminApi.deleteUK(id);
      }

      loadData(); // Перезагружаем данные
    } catch (error) {
      console.error('Error deleting item:', error);
      // TODO: Показать уведомление об ошибке
    }
  };

  return (
    <div className="space-y-6">
             <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
         <div>
           <h1 className="text-2xl md:text-3xl font-bold text-base-content">
             Панель администратора
           </h1>
           <p className="text-sm text-base-content/70 mt-2">Управление системой и настройками</p>
         </div>
         <div className="flex items-center gap-4">
           {loading && (
             <div className="loading loading-spinner loading-sm"></div>
           )}
           <div className="text-right">
             <div className="text-sm text-base-content/70">Добро пожаловать</div>
             <div className="font-medium text-base-content">{user.login}</div>
           </div>
         </div>
       </div>

             {/* Вкладки */}
       <div className="border-b border-base-300">
         <nav className="-mb-px flex flex-wrap gap-2 md:gap-8">
           <button
             className={`py-2 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${activeTab === 'users'
               ? 'border-primary text-primary'
               : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
               }`}
             onClick={() => setActiveTab('users')}
           >
             <div className="flex items-center gap-1 md:gap-2">
               <UserPlusIcon className="h-4 w-4 md:h-5 md:w-5" />
               <span className="hidden sm:inline">Пользователи</span>
               <span className="sm:hidden">Польз.</span>
             </div>
           </button>
           <button
             className={`py-2 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${activeTab === 'tariffs'
               ? 'border-primary text-primary'
               : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
               }`}
             onClick={() => setActiveTab('tariffs')}
           >
             <div className="flex items-center gap-1 md:gap-2">
               <PlusIcon className="h-4 w-4 md:h-5 md:w-5" />
               <span className="hidden sm:inline">Тарифные планы</span>
               <span className="sm:hidden">Тарифы</span>
             </div>
           </button>
           <button
             className={`py-2 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${activeTab === 'products'
               ? 'border-primary text-primary'
               : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
               }`}
             onClick={() => setActiveTab('products')}
           >
             <div className="flex items-center gap-1 md:gap-2">
               <div className="w-4 h-4 md:w-5 md:h-5 bg-accent rounded flex items-center justify-center">
                 <span className="text-accent-content text-xs">📦</span>
               </div>
               <span className="hidden sm:inline">Услуги</span>
               <span className="sm:hidden">Услуги</span>
             </div>
           </button>
           <button
             className={`py-2 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${activeTab === 'sources'
               ? 'border-primary text-primary'
               : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
               }`}
             onClick={() => setActiveTab('sources')}
           >
             <div className="flex items-center gap-1 md:gap-2">
               <div className="w-4 h-4 md:w-5 md:h-5 bg-accent rounded flex items-center justify-center">
                 <span className="text-accent-content text-xs">🔗</span>
               </div>
               <span className="hidden sm:inline">Источники</span>
               <span className="sm:hidden">Ист.</span>
             </div>
           </button>
           <button
             className={`py-2 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap ${activeTab === 'uks'
               ? 'border-primary text-primary'
               : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
               }`}
             onClick={() => setActiveTab('uks')}
           >
             <div className="flex items-center gap-1 md:gap-2">
               <div className="w-4 h-4 md:w-5 md:h-5 bg-warning rounded flex items-center justify-center">
                 <span className="text-warning-content text-xs">🏢</span>
               </div>
               <span className="hidden sm:inline">УК</span>
               <span className="sm:hidden">УК</span>
             </div>
           </button>
         </nav>
       </div>

      {/* Вкладка пользователей */}
      {activeTab === 'users' && (
        <div className="space-y-6">
                     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
             <div>
               <h2 className="text-xl md:text-2xl font-bold text-base-content">
                 Пользователи
               </h2>
               <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">Управление пользователями системы</p>
             </div>
             <button
               className="btn btn-primary w-full sm:w-auto"
               onClick={() => setShowUserForm(true)}
             >
               Создать пользователя
             </button>
           </div>

                     {/* Таблица пользователей */}
           <div className="overflow-x-auto">
             <table className="table table-zebra w-full">
               <thead>
                 <tr className="bg-base-200">
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Логин</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Тип</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">ID</th>
                   <th className="hidden md:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Город</th>
                   <th className="hidden lg:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">УК</th>
                   <th className="hidden lg:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Источники</th>
                   <th className="hidden md:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Дата создания</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Действия</th>
                 </tr>
               </thead>
               <tbody>
                 {users.map((user) => (
                   <tr key={user.id}>
                     <td className="font-semibold text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{user.login}</td>
                                           <td className="py-2 md:py-3 px-2 md:px-4">
                        <span className={`text-xs md:text-sm font-medium ${user.type === 'admin' ? 'text-error' :
                          user.type === 'manager' ? 'text-warning' :
                            'text-info'
                          }`}>
                        {user.type === 'admin'
                          ? 'Админ'
                          : user.type === 'manager'
                            ? 'Менеджер'
                            : user.type === 'salary'
                              ? 'Зарплата'
                              : 'Клиент'}
                        </span>
                      </td>
                     <td className="font-mono text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{user.agentId}</td>
                     <td className="hidden md:table-cell font-medium text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{user.category !== undefined && user.category !== null ? getCityNameById(user.category) : '-'}</td>
                     <td className="hidden lg:table-cell py-2 md:py-3 px-2 md:px-4">
                       <div className="flex flex-wrap gap-1">
                         {user.uks?.map((uk) => (
                           <span key={uk.id} className="text-xs md:text-sm font-medium text-primary">
                             {uk.uk.name}
                           </span>
                         )) || (
                           <span className="text-xs md:text-sm text-base-content/60">Не выбрано</span>
                         )}
                       </div>
                     </td>
                     <td className="hidden lg:table-cell py-2 md:py-3 px-2 md:px-4">
                       <div className="flex flex-wrap gap-1">
                         {user.sources?.map((source) => (
                           <span key={source.id} className="text-xs md:text-sm font-medium text-base-content/70">
                             {source.source.name}
                           </span>
                         )) || (
                           <span className="text-xs md:text-sm text-base-content/60">Не выбрано</span>
                         )}
                       </div>
                     </td>
                     <td className="hidden md:table-cell font-medium text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{new Date(user.createdAt).toLocaleDateString()}</td>
                     <td className="py-2 md:py-3 px-2 md:px-4">
                       <div className="flex gap-1 md:gap-2">
                         <button
                           className="btn btn-xs md:btn-sm btn-ghost"
                           onClick={() => handleEdit(user, 'user')}
                         >
                           <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                         </button>
                         <button
                           className="btn btn-xs md:btn-sm btn-ghost text-error"
                           onClick={() => handleDelete(user.id, 'user')}
                         >
                           <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
        </div>
      )}

      {/* Вкладка тарифов */}
      {activeTab === 'tariffs' && (
        <div className="space-y-6">
                     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
             <div>
               <h2 className="text-xl md:text-2xl font-bold text-base-content">
                 Тарифные планы
               </h2>
               <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">Управление тарифами для расчета вознаграждений</p>
             </div>
             <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
               <button
                 className={`btn w-full sm:w-auto ${generalTariff && generalTariff.id ? 'btn-success' : 'btn-outline'}`}
                 onClick={() => {
                   if (generalTariff && generalTariff.id) {
                     handleEditGeneralTariff();
                   } else {
                     setShowGeneralTariffForm(true);
                   }
                 }}
               >
                 Общий тариф {generalTariff && generalTariff.id && '✓'}
               </button>
               <button
                 className="btn btn-primary w-full sm:w-auto"
                 onClick={() => setShowTariffForm(true)}
               >
                 Создать тариф
               </button>
             </div>
           </div>

                     {/* Таблица тарифов */}
           <div className="overflow-x-auto">
             <table className="table table-zebra w-full">
               <thead>
                 <tr className="bg-base-200">
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Название</th>
                   <th className="hidden md:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">УК</th>
                   <th className="hidden lg:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Источники</th>
                   <th className="hidden lg:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Ставки</th>
                   <th className="hidden md:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Спец. товары</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Статус</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Действия</th>
                 </tr>
               </thead>
               <tbody>
                 {tariffs.map((tariff) => (
                   <tr key={tariff.id}>
                     <td className="py-2 md:py-3 px-2 md:px-4">
                       <div className="font-bold text-xs md:text-sm text-base-content">{tariff.name}</div>
                       {tariff.description && (
                         <div className="text-xs md:text-sm font-medium text-base-content/70 mt-1 md:mt-2">{tariff.description}</div>
                       )}
                     </td>
                                           <td className="hidden md:table-cell py-2 md:py-3 px-2 md:px-4">
                        <div className="flex flex-wrap gap-1">
                          {tariff.uks?.map((uk) => (
                            <span key={uk.id} className="text-xs md:text-sm font-medium text-primary">
                              {uk.uk.name}
                            </span>
                          )) || (
                            <span className="text-xs md:text-sm text-base-content/60">Не выбрано</span>
                          )}
                        </div>
                      </td>
                     <td className="hidden lg:table-cell py-2 md:py-3 px-2 md:px-4">
                                               <div className="flex flex-wrap gap-1">
                          {tariff.sources?.map((source) => (
                            <span key={source.sourceId} className="text-xs md:text-sm font-medium text-base-content/70">
                              {source.source.name}
                            </span>
                          )) || (
                              <span className="text-xs md:text-sm text-base-content/60">Не выбрано</span>
                            )}
                        </div>
                     </td>
                     <td className="hidden lg:table-cell py-2 md:py-3 px-2 md:px-4">
                       <div className="space-y-1">
                         <div className="flex items-center gap-1 md:gap-2">
                           <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-info"></span>
                           <span className="text-xs md:text-sm text-base-content/70">Вода: <span className="font-medium">{tariff.waterRate}%</span></span>
                         </div>
                         <div className="flex items-center gap-1 md:gap-2">
                           <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-warning"></span>
                           <span className="text-xs md:text-sm text-base-content/70">Тепло: <span className="font-medium">{tariff.heatRate}%</span></span>
                         </div>
                         <div className="flex items-center gap-1 md:gap-2">
                           <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-error"></span>
                           <span className="text-xs md:text-sm text-base-content/70">Газ: <span className="font-medium">{tariff.gasRate}%</span></span>
                         </div>
                       </div>
                     </td>
                     <td className="hidden md:table-cell py-2 md:py-3 px-2 md:px-4">
                                               {tariff.products && tariff.products.length > 0 ? (
                          <div className="flex items-center gap-1 md:gap-2">
                            <span className="text-xs md:text-sm font-medium text-success">
                              {tariff.products.length}
                            </span>
                            <span className="text-xs md:text-sm text-base-content/70">товар(ов)</span>
                          </div>
                        ) : (
                          <span className="text-xs md:text-sm text-base-content/60">Базовые ставки</span>
                        )}
                     </td>
                                           <td className="py-2 md:py-3 px-2 md:px-4">
                        <span className={`text-xs md:text-sm font-medium ${tariff.isActive
                          ? 'text-success'
                          : 'text-error'
                          }`}>
                          {tariff.isActive ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                     <td className="py-2 md:py-3 px-2 md:px-4">
                       <div className="flex gap-1 md:gap-2">
                         <button
                           className="btn btn-xs md:btn-sm btn-ghost"
                           onClick={() => handleEdit(tariff, 'tariff')}
                         >
                           <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                         </button>
                         <button
                           className="btn btn-xs md:btn-sm btn-ghost text-error"
                           onClick={() => handleDelete(tariff.id, 'tariff')}
                         >
                           <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
        </div>
      )}

      {/* Вкладка товаров */}
      {activeTab === 'products' && (
        <div className="space-y-6">
                     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
             <div>
               <h2 className="text-xl md:text-2xl font-bold text-base-content">
                 Услуги
               </h2>
               <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">Управление товарами для тарифных планов</p>
             </div>
             <button
               className="btn btn-primary w-full sm:w-auto"
               onClick={() => setShowProductForm(true)}
             >
               Добавить товар
             </button>
           </div>

                     {/* Таблица товаров */}
           <div className="overflow-x-auto">
             <table className="table table-zebra w-full">
               <thead>
                 <tr className="bg-base-200">
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Наименование</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Тип</th>
                   <th className="hidden md:table-cell text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Описание</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Статус</th>
                   <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Действия</th>
                 </tr>
               </thead>
               <tbody>
                 {products.map((product) => (
                   <tr key={product.id}>
                     <td className="font-bold text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{product.name}</td>
                                           <td className="py-2 md:py-3 px-2 md:px-4">
                        <span className={`text-xs md:text-sm font-medium ${product.type === 'water' ? 'text-info' :
                          product.type === 'warm' ? 'text-warning' : 'text-error'
                          }`}>
                          {product.type === 'water' ? 'Вода' : 
                           product.type === 'warm' ? 'Тепло' : 
                           product.type === 'gas' ? 'Газ' : 'Другое'}
                        </span>
                      </td>
                     <td className="hidden md:table-cell font-medium text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{product.description || '-'}</td>
                                           <td className="py-2 md:py-3 px-2 md:px-4">
                        <span className={`text-xs md:text-sm font-medium ${
                          product.isHidden ? 'text-warning' : 
                          product.isActive ? 'text-success' : 'text-error'
                        }`}>
                          {product.isHidden ? 'Скрыт' : 
                           product.isActive ? 'Активен' : 'Неактивен'}
                        </span>
                        {product.hiddenForClientIds && product.hiddenForClientIds.length > 0 && !product.isHidden && (
                          <div className="text-[11px] md:text-xs text-base-content/70 mt-1">
                            Скрыт у {product.hiddenForClientIds.length} аккаунт{product.hiddenForClientIds.length === 1 ? 'а' : 'ов'}
                          </div>
                        )}
                      </td>
                     <td className="py-2 md:py-3 px-2 md:px-4">
                       <div className="flex gap-1 md:gap-2">
                         <button
                           className="btn btn-xs md:btn-sm btn-ghost"
                           onClick={() => handleEdit(product, 'product')}
                         >
                           <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                         </button>
                         <button
                           className="btn btn-xs md:btn-sm btn-ghost text-error"
                           onClick={() => handleDelete(product.id, 'product')}
                         >
                           <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                         </button>
                       </div>
                     </td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </div>
        </div>
      )}

      {/* Вкладка источников */}
      {activeTab === 'sources' && (
        <div className="space-y-6">
                     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
             <div>
               <h2 className="text-xl md:text-2xl font-bold text-base-content">
                 Источники
               </h2>
               <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">Управление источниками для тарифных планов</p>
             </div>
             <button
               className="btn btn-primary w-full sm:w-auto"
               onClick={() => setShowSourceForm(true)}
             >
               Добавить источник
             </button>
           </div>

                     {/* Поиск по источникам */}
           <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
             <div className="form-control flex-1 w-full sm:max-w-md">
               <input
                 type="text"
                 placeholder="Поиск по названию, ID статуса или сортировке..."
                 className="input input-bordered w-full"
                 value={sourceSearchTerm}
                 onChange={(e) => setSourceSearchTerm(e.target.value)}
               />
             </div>
             {sourceSearchTerm && (
               <div className="text-sm text-base-content/70 self-start sm:self-center">
                 Найдено: {filteredSources.length} из {sources.length}
               </div>
             )}
           </div>

          {/* Таблица источников */}
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-200">
                  <th className="text-base font-bold text-base-content py-3 px-4">Название</th>
                  <th className="text-base font-bold text-base-content py-3 px-4">Сортировка</th>
                  <th className="text-base font-bold text-base-content py-3 px-4">ID статуса</th>
                  <th className="text-base font-bold text-base-content py-3 px-4">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredSources.map((source) => (
                  <tr key={source.id}>
                    <td className="font-bold text-sm text-base-content">{source.name}</td>
                    <td className="font-medium text-sm text-base-content">{source.sort}</td>
                    <td className="font-mono text-sm text-base-content">{source.statusId}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleEdit(source, 'source')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="btn btn-sm btn-ghost text-error"
                          onClick={() => handleDelete(source.id, 'source')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredSources.length === 0 && sourceSearchTerm && (
              <div className="text-center py-8 text-base-content/60">
                <div className="text-4xl mb-2">🔍</div>
                <p className="text-base-content/80">По запросу "{sourceSearchTerm}" ничего не найдено</p>
                <p className="text-sm text-base-content/70">Попробуйте изменить поисковый запрос</p>
              </div>
            )}
          </div>
        </div>
      )}

             {/* Модальное окно создания/редактирования пользователя */}
       {showUserForm && (
         <div className="modal modal-open">
           <div className="modal-box w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-base-content">
                {editingItem ? 'Редактировать пользователя' : 'Создать пользователя'}
              </h3>
              <button
                onClick={() => {
                  setShowUserForm(false);
                  setEditingItem(null);
                  setUserForm({ login: '', password: '', type: 'client', agentId: '', category: '', salaryCategories: [], ukIds: [], sourceIds: [], disableRewards: false });
                  setDisplaySettings({
                    showAccountNumber: false,
                    showComments: false,
                    showRefusalReason: false,
                    showCounterType: false,
                    showSerialNumber: false,
                    showReadings: false,
                    showDealId: false,
                    showTPlusReport: false,
                  });
                }}
                className="btn btn-ghost btn-sm btn-circle"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-6">
              {/* Основная информация */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-primary rounded-full"></span>
                  Основная информация
                </h4>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Логин *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input input-bordered flex-1"
                        value={userForm.login}
                        onChange={(e) => setUserForm({ ...userForm, login: e.target.value })}
                        placeholder="Введите логин пользователя"
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm h-12"
                        onClick={() => navigator.clipboard.writeText(userForm.login)}
                        title="Копировать логин"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Пароль {editingItem ? '(оставьте пустым, чтобы не менять)' : '*'}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input input-bordered flex-1"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        placeholder={editingItem ? 'Введите новый пароль или оставьте пустым' : 'Введите пароль'}
                        minLength={8}
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm h-12"
                        onClick={generatePassword}
                        title="Сгенерировать пароль"
                      >
                        🎲
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm h-12"
                        onClick={() => navigator.clipboard.writeText(userForm.password)}
                        title="Копировать пароль"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Тип пользователя *
                    </label>
                    <Dropdown
                      value={userForm.type}
                      onChange={(e) => {
                        const newType = e.value as any;
                        setUserForm({
                          ...userForm,
                          type: newType,
                          // Очищаем категорию если выбран администратор
                          category: newType === 'admin' || newType === 'salary' ? '' : userForm.category,
                          salaryCategories: newType === 'salary' ? userForm.salaryCategories : []
                        });
                      }}
                      options={[
                        { label: 'Клиент', value: 'client' },
                        { label: 'Менеджер', value: 'manager' },
                        { label: 'Зарплата', value: 'salary' },
                        { label: 'Администратор', value: 'admin' }
                      ]}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Выберите тип пользователя"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      ID *
                    </label>
                    <input
                      type="number"
                      className="input input-bordered w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      value={userForm.agentId}
                      onChange={(e) => setUserForm({ ...userForm, agentId: e.target.value })}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="Внутренний ID для поиска"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Поле "Город" показывается для клиентов и менеджеров, скрывается для администраторов */}
              {(userForm.type === 'manager' || userForm.type === 'client') && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-secondary rounded-full"></span>
                    Местоположение
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Город *
                    </label>
                                         <Dropdown
                       value={userForm.category}
                       onChange={(e) => setUserForm({ ...userForm, category: e.value || '' })}
                       options={USER_CITY_OPTIONS}
                       optionLabel="label"
                       optionValue="value"
                       placeholder="Выберите город"
                       className="w-full"
                       showClear
                     />
                    <div className="text-sm text-base-content/70 mt-2 flex items-center gap-2">
                      <span className="w-3 h-3 bg-secondary rounded-full"></span>
                      {userForm.type === 'client'
                        ? 'Город определяет доступ к данным по конкретному региону'
                        : 'Город определяет доступ к данным по конкретному региону'
                      }
                    </div>
                  </div>
                </div>
              )}

              {userForm.type === 'salary' && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-secondary rounded-full"></span>
                    Доступные города
                  </h4>
                  <label className="block text-sm font-medium text-base-content mb-2">
                    Города (можно выбрать несколько)
                  </label>
                  <MultiSelect
                    value={userForm.salaryCategories}
                    onChange={(e) => setUserForm({ ...userForm, salaryCategories: e.value || [] })}
                    options={USER_CITY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Выберите города"
                    className="w-full"
                    filter
                    showClear
                    display="chip"
                  />
                </div>
              )}

              {/* Поля для клиентов */}
              {userForm.type === 'client' && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-success rounded-full"></span>
                    Информация о клиенте
                  </h4>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-base-content mb-2">
                        УК
                      </label>
                      {uks && uks.length > 0 ? (
                        <MultiSelect
                          value={userForm.ukIds}
                          onChange={(e) => setUserForm({ ...userForm, ukIds: e.value || [] })}
                          options={uks.map(uk => ({ label: uk.name, value: uk.id }))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Выберите УК"
                          className="w-full"
                          filter
                          showClear
                          display="chip"
                        />
                      ) : (
                        <div className="alert alert-warning">
                          <span>УК загружаются...</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-base-content mb-2">
                        Источники *
                      </label>
                      {sources && sources.length > 0 ? (
                        <MultiSelect
                          value={userForm.sourceIds}
                          onChange={(e) => setUserForm({ ...userForm, sourceIds: e.value || [] })}
                          options={sources.map(source => ({ label: source.name, value: source.id }))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Выберите источники"
                          className="w-full"
                          filter
                          showClear
                          display="chip"
                        />
                      ) : (
                        <div className="alert alert-warning">
                          <span>Источники загружаются...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Поля для менеджеров */}
              {userForm.type === 'manager' && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-info rounded-full"></span>
                    Информация о менеджере
                  </h4>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-base-content mb-2">
                        УК
                      </label>
                      {uks && uks.length > 0 ? (
                        <MultiSelect
                          value={userForm.ukIds}
                          onChange={(e) => setUserForm({ ...userForm, ukIds: e.value || [] })}
                          options={uks.map(uk => ({ label: uk.name, value: uk.id }))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Выберите УК"
                          className="w-full"
                          filter
                          showClear
                          display="chip"
                        />
                      ) : (
                        <div className="alert alert-warning">
                          <span>УК загружаются...</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-base-content mb-2">
                        Источники *
                      </label>
                      {sources && sources.length > 0 ? (
                        <MultiSelect
                          value={userForm.sourceIds}
                          onChange={(e) => setUserForm({ ...userForm, sourceIds: e.value || [] })}
                          options={sources.map(source => ({ label: source.name, value: source.id }))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Выберите источники"
                          className="w-full"
                          filter
                          showClear
                          display="chip"
                        />
                      ) : (
                        <div className="alert alert-warning">
                          <span>Источники загружаются...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Дополнительные поля - показывается для клиентов и менеджеров */}
              {(userForm.type === 'client' || userForm.type === 'manager') && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-warning rounded-full"></span>
                    Дополнительные поля
                  </h4>
                  <div className="space-y-4">
                    <p className="text-sm text-base-content/70 mb-4">
                      Выберите дополнительные поля, которые будут отображаться в детальной информации о сделках
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Номер Л/С</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showAccountNumber}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showAccountNumber: e.target.checked })}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Комментарии</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showComments}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showComments: e.target.checked })}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Причина отказа</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showRefusalReason}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showRefusalReason: e.target.checked })}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Тип счетчика</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showCounterType}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showCounterType: e.target.checked })}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Заводской номер</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showSerialNumber}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showSerialNumber: e.target.checked })}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">Показания</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showReadings}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showReadings: e.target.checked })}
                          />
                        </label>
                      </div>

                      <div className="form-control">
                        <label className="label cursor-pointer">
                          <span className="label-text">ID сделки</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary"
                            checked={displaySettings.showDealId}
                            onChange={(e) => setDisplaySettings({ ...displaySettings, showDealId: e.target.checked })}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Дополнительные отчеты - показывается для клиентов и менеджеров */}
              {(userForm.type === 'client' || userForm.type === 'manager') && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-secondary rounded-full"></span>
                    Дополнительные отчеты
                  </h4>
                  <div className="space-y-4">
                    <p className="text-sm text-base-content/70">
                      Включите дополнительные отчеты, которые будут доступны пользователю в панели сделок
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => setDisplaySettings({ ...displaySettings, showTPlusReport: !displaySettings.showTPlusReport })}
                        className={`btn ${displaySettings.showTPlusReport ? 'btn-primary' : 'btn-outline'}`}
                      >
                        Т-Плюс
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Вознаграждения - показывается для клиентов и менеджеров */}
              {(userForm.type === 'client' || userForm.type === 'manager') && (
                <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                  <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                    <span className="w-3 h-3 bg-success rounded-full"></span>
                    Вознаграждения
                  </h4>
                  <div className="space-y-4">
                    <p className="text-sm text-base-content/70 mb-4">
                      Настройка отображения информации о вознаграждениях
                    </p>
                    
                    <div className="form-control">
                      <label className="label cursor-pointer">
                        <span className="label-text">Отключить вознаграждения</span>
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary"
                          checked={userForm.disableRewards || false}
                          onChange={(e) => setUserForm({ ...userForm, disableRewards: e.target.checked })}
                        />
                      </label>
                      <div className="label">
                        <span className="label-text-alt text-base-content/60">
                          Если включено, то поля "Общая сумма", "Вознаграждение", "Среднее вознаграждение", 
                          колонки "Сумма" и "Вознаграждение*" в таблице сделок, а также поля "Цена", "Количество", 
                          "Вознаграждение" и "Ставка" в раскрытой сделке будут скрыты
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
                         <div className="modal-action mt-8 flex-col sm:flex-row gap-2">
               <button
                 className="btn btn-ghost btn-lg w-full sm:w-auto"
                 onClick={() => {
                   setShowUserForm(false);
                   setEditingItem(null);
                   setUserForm({ login: '', password: '', type: 'client', agentId: '', category: '', salaryCategories: [], ukIds: [], sourceIds: [], disableRewards: false });
    setDisplaySettings({
      showAccountNumber: false,
      showComments: false,
      showRefusalReason: false,
      showCounterType: false,
      showSerialNumber: false,
      showReadings: false,
      showDealId: false,
      showTPlusReport: false,
    });
                 }}
               >
                 Отмена
               </button>
               <button
                 className="btn btn-primary btn-lg w-full sm:w-auto"
                 onClick={handleCreateUser}
                 disabled={
                   !userForm.login ||
                   (!editingItem && !userForm.password) ||
                   !userForm.type ||
                   !userForm.agentId ||
                   ((userForm.type === 'manager' || userForm.type === 'client') && userForm.category === '') ||
                   (!editingItem && userForm.type === 'salary' && userForm.salaryCategories.length === 0) ||
                   (userForm.type === 'client' && userForm.sourceIds.length === 0) ||
                   (userForm.type === 'manager' && (userForm.ukIds.length === 0 || userForm.sourceIds.length === 0))
                 }
               >
                 {editingItem ? 'Сохранить изменения' : 'Создать пользователя'}
               </button>
             </div>
          </div>
        </div>
      )}

             {/* Модальное окно создания/редактирования тарифа */}
       {showTariffForm && (
         <div className="modal modal-open">
           <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-base-content">
                {editingItem ? 'Редактировать тариф' : 'Создать новый тариф'}
              </h3>
              <button
                onClick={() => {
                  setShowTariffForm(false);
                  setEditingItem(null);
                  setTariffForm({
                    name: '',
                    waterRate: 0,
                    heatRate: 0,
                    gasRate: 0,
                    description: '',
                    sourceIds: [],
                    ukIds: [],
                    products: []
                  });
                }}
                className="btn btn-ghost btn-sm btn-circle"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Основная информация */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-success rounded-full"></span>
                  Основная информация
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Название тарифа *
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={tariffForm.name}
                      onChange={(e) => setTariffForm({ ...tariffForm, name: e.target.value })}
                      placeholder="Введите название тарифа"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Управляющие компании (опционально)
                    </label>
                    <MultiSelect
                      value={tariffForm.ukIds}
                      onChange={(e) => setTariffForm({ ...tariffForm, ukIds: e.value || [] })}
                      options={uks.map(uk => ({ label: uk.name, value: uk.id }))}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Выберите УК"
                      className="w-full"
                      filter
                      showClear
                      display="chip"
                    />
                  </div>
                </div>
              </div>

              {/* Источники */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-accent rounded-full"></span>
                  Источники *
                </h4>
                {sources && sources.length > 0 ? (
                  <MultiSelect
                    value={tariffForm.sourceIds}
                    onChange={(e) => setTariffForm({ ...tariffForm, sourceIds: e.value || [] })}
                    options={sources.map(source => ({ label: source.name, value: source.id }))}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Выберите источники"
                    className="w-full"
                    filter
                    showClear
                    display="chip"
                  />
                ) : (
                  <div className="alert alert-warning">
                    <span>Источники загружаются...</span>
                  </div>
                )}
                <div className="mt-2 text-sm text-base-content/70">
                  Можно выбрать несколько источников. Используйте поиск для быстрого нахождения.
                </div>
              </div>

              {/* Базовые ставки */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-info rounded-full"></span>
                  Базовые процентные ставки
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Ставка для воды (%)
                    </label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={tariffForm.waterRate}
                      onChange={(e) => setTariffForm({ ...tariffForm, waterRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Ставка для тепла (%)
                    </label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={tariffForm.heatRate}
                      onChange={(e) => setTariffForm({ ...tariffForm, heatRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0.0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Ставка для газа (%)
                    </label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={tariffForm.gasRate}
                      onChange={(e) => setTariffForm({ ...tariffForm, gasRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>

              {/* Специальные товары */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-base-content flex items-center gap-2">
                    <span className="w-3 h-3 bg-warning rounded-full"></span>
                    Специальные товары
                  </h4>
                  <button
                    className="btn btn-sm btn-outline btn-primary"
                    onClick={() => {
                      setTariffForm({
                        ...tariffForm,
                        products: [
                          ...tariffForm.products,
                          { productId: '', rate: 0, rateType: 'fixed' as const }
                        ]
                      });
                    }}
                  >
                    Добавить товар
                  </button>
                </div>

                {tariffForm.products.length === 0 ? (
                  <div className="text-center py-8 text-base-content/60">
                    <div className="text-4xl mb-2">📦</div>
                    <p className="text-base-content/80">Специальные товары не добавлены</p>
                    <p className="text-sm text-base-content/70">Используются базовые ставки по типам</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tariffForm.products.map((product, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-base-200">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-base-content">Товар #{index + 1}</h5>
                          <button
                            className="btn btn-sm btn-ghost btn-circle btn-error"
                            onClick={() => {
                              const newProducts = tariffForm.products.filter((_, i) => i !== index);
                              setTariffForm({ ...tariffForm, products: newProducts });
                            }}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-base-content mb-2">
                              Товар *
                            </label>
                            <Dropdown
                              value={product.productId}
                              onChange={(e) => {
                                const newProducts = [...tariffForm.products];
                                newProducts[index].productId = e.value;
                                setTariffForm({ ...tariffForm, products: newProducts });
                              }}
                              options={products.map(p => ({ label: p.name, value: p.id }))}
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Выберите товар"
                              className="w-full"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-base-content mb-2">
                              Тип ставки
                            </label>
                            <Dropdown
                              value={product.rateType}
                              onChange={(e) => {
                                const newProducts = [...tariffForm.products];
                                newProducts[index].rateType = e.value as 'fixed' | 'percentage';
                                setTariffForm({ ...tariffForm, products: newProducts });
                              }}
                              options={[
                                { label: 'Фиксированная (₽)', value: 'fixed' },
                                { label: 'Процентная (%)', value: 'percentage' }
                              ]}
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Выберите тип ставки"
                              className="w-full"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-base-content mb-2">
                              Ставка *
                            </label>
                            <input
                              type="number"
                              className="input input-bordered w-full"
                              value={product.rate}
                              onChange={(e) => {
                                const newProducts = [...tariffForm.products];
                                newProducts[index].rate = parseFloat(e.target.value) || 0;
                                setTariffForm({ ...tariffForm, products: newProducts });
                              }}
                              min="0"
                              step="0.01"
                              placeholder={product.rateType === 'fixed' ? '0.00 ₽' : '0.00 %'}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Описание */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-secondary rounded-full"></span>
                  Описание
                </h4>
                <textarea
                  className="textarea textarea-bordered w-full"
                  value={tariffForm.description}
                  onChange={(e) => setTariffForm({ ...tariffForm, description: e.target.value })}
                  rows={3}
                  placeholder="Описание тарифа (необязательно)"
                />
              </div>
            </div>
                         <div className="modal-action mt-8 flex-col sm:flex-row gap-2">
               <button
                 className="btn btn-ghost btn-lg w-full sm:w-auto"
                 onClick={() => {
                   setShowTariffForm(false);
                   setEditingItem(null);
                   setTariffForm({
                     name: '',
                     waterRate: 0,
                     heatRate: 0,
                     gasRate: 0,
                     description: '',
                     sourceIds: [],
                     ukIds: [],
                     products: []
                   });
                 }}
               >
                 Отмена
               </button>
               <button
                 className="btn btn-primary btn-lg w-full sm:w-auto"
                 onClick={handleCreateTariff}
                 disabled={!tariffForm.name || tariffForm.sourceIds.length === 0}
               >
                 {editingItem ? 'Сохранить изменения' : 'Создать тариф'}
               </button>
             </div>
          </div>
        </div>
      )}

      {/* Модальное окно создания/редактирования общего тарифа */}
      {showGeneralTariffForm && (
        <div className="modal modal-open">
          <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-base-content">
                {generalTariff && generalTariff.id ? 'Редактировать общий тариф' : 'Создать общий тариф'}
              </h3>
              <button
                onClick={() => {
                  setShowGeneralTariffForm(false);
                  setGeneralTariffForm({
                    waterRate: 0,
                    heatRate: 0,
                    gasRate: 0,
                    products: []
                  });
                }}
                className="btn btn-ghost btn-sm btn-circle"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Базовые процентные ставки */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-success rounded-full"></span>
                  Базовые процентные ставки
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Вода (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="input input-bordered w-full"
                      value={generalTariffForm.waterRate}
                      onChange={(e) => setGeneralTariffForm({ ...generalTariffForm, waterRate: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Тепло (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="input input-bordered w-full"
                      value={generalTariffForm.heatRate}
                      onChange={(e) => setGeneralTariffForm({ ...generalTariffForm, heatRate: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Газ (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="input input-bordered w-full"
                      value={generalTariffForm.gasRate}
                      onChange={(e) => setGeneralTariffForm({ ...generalTariffForm, gasRate: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Специальные товары */}
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-base-content flex items-center gap-2">
                    <span className="w-3 h-3 bg-info rounded-full"></span>
                    Специальные товары
                  </h4>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => setGeneralTariffForm({
                      ...generalTariffForm,
                      products: [...generalTariffForm.products, { productId: '', rate: 0, rateType: 'fixed' as const }]
                    })}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Добавить товар
                  </button>
                </div>

                {generalTariffForm.products.length === 0 ? (
                  <div className="text-center py-8 text-base-content/60">
                    <div className="text-4xl mb-2">📦</div>
                    <p className="text-base-content/80">Специальные товары не добавлены</p>
                    <p className="text-sm text-base-content/70">Используются базовые ставки по типам</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {generalTariffForm.products.map((product, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-base-200">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-base-content">Товар #{index + 1}</h5>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost btn-circle btn-error"
                            onClick={() => {
                              const newProducts = generalTariffForm.products.filter((_, i) => i !== index);
                              setGeneralTariffForm({ ...generalTariffForm, products: newProducts });
                            }}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-base-content mb-2">
                              Товар *
                            </label>
                            <Dropdown
                              value={product.productId}
                              onChange={(e) => {
                                const newProducts = [...generalTariffForm.products];
                                newProducts[index].productId = e.value;
                                setGeneralTariffForm({ ...generalTariffForm, products: newProducts });
                              }}
                              options={products.map(p => ({ label: p.name, value: p.id }))}
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Выберите товар"
                              className="w-full"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-base-content mb-2">
                              Тип ставки
                            </label>
                            <Dropdown
                              value={product.rateType}
                              onChange={(e) => {
                                const newProducts = [...generalTariffForm.products];
                                newProducts[index].rateType = e.value as 'fixed' | 'percentage';
                                setGeneralTariffForm({ ...generalTariffForm, products: newProducts });
                              }}
                              options={[
                                { label: 'Фиксированная (₽)', value: 'fixed' },
                                { label: 'Процентная (%)', value: 'percentage' }
                              ]}
                              optionLabel="label"
                              optionValue="value"
                              placeholder="Выберите тип ставки"
                              className="w-full"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-base-content mb-2">
                              Ставка *
                            </label>
                            <input
                              type="number"
                              className="input input-bordered w-full"
                              value={product.rate}
                              onChange={(e) => {
                                const newProducts = [...generalTariffForm.products];
                                newProducts[index].rate = parseFloat(e.target.value) || 0;
                                setGeneralTariffForm({ ...generalTariffForm, products: newProducts });
                              }}
                              min="0"
                              step="0.01"
                              placeholder={product.rateType === 'fixed' ? '0.00 ₽' : '0.00 %'}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-action">
              <div className="flex gap-2 w-full">
                {generalTariff && generalTariff.id && (
                  <button
                    className="btn btn-error btn-lg w-full sm:w-auto"
                    onClick={handleDeleteGeneralTariff}
                  >
                    Удалить тариф
                  </button>
                )}
                <button
                  className="btn btn-primary btn-lg w-full sm:w-auto"
                  onClick={handleCreateGeneralTariff}
                >
                  {generalTariff && generalTariff.id ? 'Сохранить изменения' : 'Создать общий тариф'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

             {/* Модальное окно создания/редактирования товара */}
       {showProductForm && (
         <div className="modal modal-open">
           <div className="modal-box w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-base-content">
                {editingItem ? 'Редактировать товар' : 'Добавить товар'}
              </h3>
              <button
                onClick={() => {
                  setShowProductForm(false);
                  setEditingItem(null);
                  setProductForm(initialProductForm);
                }}
                className="btn btn-ghost btn-sm btn-circle"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-warning rounded-full"></span>
                  Основная информация
                </h4>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Наименование товара *
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      placeholder="Введите название товара"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Тип товара *
                    </label>
                    <Dropdown
                      value={productForm.type}
                      onChange={(e) => setProductForm({ ...productForm, type: e.value as 'water' | 'warm' | 'gas' | 'other' })}
                      options={[
                        { label: 'Вода', value: 'water' },
                        { label: 'Тепло', value: 'warm' },
                        { label: 'Газ', value: 'gas' },
                        { label: 'Другое', value: 'other' }
                      ]}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Выберите тип товара"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Описание
                    </label>
                    <textarea
                      className="textarea textarea-bordered w-full"
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      rows={3}
                      placeholder="Описание товара (необязательно)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Скрыть товар у учетных записей
                    </label>
                    <MultiSelect
                      value={productForm.hiddenForClientIds}
                      onChange={(e) =>
                        setProductForm({
                          ...productForm,
                          hiddenForClientIds: e.value || [],
                          hideForAll: false,
                        })
                      }
                      options={clientOptions}
                      placeholder={
                        clientOptions.length ? 'Выберите логины клиентов' : 'Нет доступных клиентов'
                      }
                      className="w-full"
                      display="chip"
                      filter
                      showSelectAll={false}
                      disabled={!clientOptions.length || productForm.hideForAll}
                    />
                    <div className="label">
                      <span className="label-text-alt text-base-content/70">
                        Выбранные логины не увидят этот товар
                      </span>
                    </div>
                  </div>

                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text text-base-content">Скрыть товар у всех</span>
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary"
                        checked={productForm.hideForAll}
                        onChange={(e) =>
                          setProductForm({
                            ...productForm,
                            hideForAll: e.target.checked,
                            hiddenForClientIds: e.target.checked ? [] : productForm.hiddenForClientIds,
                          })
                        }
                        disabled={productForm.hiddenForClientIds.length > 0}
                      />
                    </label>
                    <div className="label">
                      <span className="label-text-alt text-base-content/70">
                        Товар полностью исключается из расчетов и отображения
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
                         <div className="modal-action mt-8 flex-col sm:flex-row gap-2">
               <button
                 className="btn btn-ghost btn-lg w-full sm:w-auto"
                 onClick={() => {
                   setShowProductForm(false);
                   setEditingItem(null);
                   setProductForm(initialProductForm);
                 }}
               >
                 Отмена
               </button>
               <button
                 className="btn btn-primary btn-lg w-full sm:w-auto"
                 onClick={handleCreateProduct}
                 disabled={!productForm.name || !productForm.type}
               >
                 {editingItem ? 'Сохранить изменения' : 'Добавить товар'}
               </button>
             </div>
          </div>
        </div>
      )}

             {/* Модальное окно создания/редактирования источника */}
       {showSourceForm && (
         <div className="modal modal-open">
           <div className="modal-box w-11/12 max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-base-content">
                {editingItem ? 'Редактировать источник' : 'Добавить источник'}
              </h3>
              <button
                onClick={() => {
                  setShowSourceForm(false);
                  setEditingItem(null);
                  setSourceForm({ name: '', sort: 0, statusId: '' });
                }}
                className="btn btn-ghost btn-sm btn-circle"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-6">
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-info rounded-full"></span>
                  Основная информация
                </h4>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Название источника *
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={sourceForm.name}
                      onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                      placeholder="Введите название источника"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Сортировка *
                    </label>
                    <input
                      type="number"
                      className="input input-bordered w-full"
                      value={sourceForm.sort}
                      onChange={(e) => setSourceForm({ ...sourceForm, sort: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      ID статуса в Bitrix24 *
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={sourceForm.statusId}
                      onChange={(e) => setSourceForm({ ...sourceForm, statusId: e.target.value })}
                      placeholder="Введите ID статуса"
                    />
                  </div>
                </div>
              </div>
            </div>
                         <div className="modal-action mt-8 flex-col sm:flex-row gap-2">
               <button
                 className="btn btn-ghost btn-lg w-full sm:w-auto"
                 onClick={() => {
                   setShowSourceForm(false);
                   setEditingItem(null);
                   setSourceForm({ name: '', sort: 0, statusId: '' });
                 }}
               >
                 Отмена
               </button>
               <button
                 className="btn btn-primary btn-lg w-full sm:w-auto"
                 onClick={handleCreateSource}
                 disabled={!sourceForm.name || sourceForm.statusId === ''}
               >
                 {editingItem ? 'Сохранить изменения' : 'Добавить источник'}
               </button>
             </div>
          </div>
        </div>
      )}

      {/* Вкладка УК */}
      {activeTab === 'uks' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-base-content">
                Управляющие компании
              </h2>
              <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">Управление справочником УК</p>
            </div>
            <button
              className="btn btn-primary w-full sm:w-auto"
              onClick={() => {
                setEditingItem(null);
                setUKForm({ name: '' });
                setShowUKForm(true);
              }}
            >
              Добавить УК
            </button>
          </div>

          {/* Поиск */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Поиск по названию УК..."
                className="input input-bordered w-full"
                value={ukSearchTerm}
                onChange={(e) => setUKSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Таблица УК */}
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-200">
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Название</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Статус</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredUKs.map((uk) => (
                  <tr key={uk.id}>
                    <td className="font-bold text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">{uk.name}</td>
                    <td className="py-2 md:py-3 px-2 md:px-4">
                      <span className={`text-xs md:text-sm font-medium ${uk.isActive ? 'text-success' : 'text-error'}`}>
                        {uk.isActive ? 'Активна' : 'Неактивна'}
                      </span>
                    </td>
                    <td className="py-2 md:py-3 px-2 md:px-4">
                      <div className="flex gap-2">
                        <button
                          className="btn btn-ghost btn-xs md:btn-sm"
                          onClick={() => handleEdit(uk, 'uk')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="btn btn-ghost btn-xs md:btn-sm text-error"
                          onClick={() => handleDelete(uk.id, 'uk')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Модальное окно для УК */}
      {showUKForm && (
        <div className="modal modal-open">
          <div className="modal-box w-11/12 max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg md:text-xl font-bold text-base-content">
                {editingItem ? 'Редактировать УК' : 'Добавить УК'}
              </h3>
              <button
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => {
                  setShowUKForm(false);
                  setEditingItem(null);
                  setUKForm({ name: '' });
                }}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-base-100 rounded-lg border border-base-300 p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-warning rounded-full"></span>
                  Информация об УК
                </h4>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-base-content mb-2">
                      Название УК *
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={ukForm.name}
                      onChange={(e) => setUKForm({ ...ukForm, name: e.target.value })}
                      placeholder="Введите название УК"
                    />
                  </div>

                </div>
              </div>
            </div>
            <div className="modal-action mt-8 flex-col sm:flex-row gap-2">
              <button
                className="btn btn-ghost btn-lg w-full sm:w-auto"
                onClick={() => {
                  setShowUKForm(false);
                  setEditingItem(null);
                  setUKForm({ name: '' });
                }}
              >
                Отмена
              </button>
              <button
                className="btn btn-primary btn-lg w-full sm:w-auto"
                onClick={handleCreateUK}
                disabled={!ukForm.name}
              >
                {editingItem ? 'Сохранить изменения' : 'Добавить УК'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
